import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

import './pie-chart-v2.scss';

export default class PieChartV2 extends React.Component {
  static propTypes = {
    data: ImmutablePropTypes.listOf(ImmutablePropTypes.map.isRequired),
    className: PropTypes.string,
    size: PropTypes.number,
    strokeWidth: PropTypes.number
  };

  static defaultProps = {
    size: 250,
    strokeWidth: 25
  };

  // Translates anlge into radians, then grabs polar coordinates.
  getPolarCoordinates(radius, angle) {
    const angleInRadians = ((angle - 90) * Math.PI) / 180.0;
    return {
      x: radius * Math.cos(angleInRadians),
      y: radius * Math.sin(angleInRadians)
    };
  }

  // Effectively just doing a transform(centerX, centerY)
  translatePolarCoordinates(coordinates, centerX, centerY) {
    return {
      x: centerX + coordinates.x,
      y: centerY - coordinates.y
    };
  }

  getPathArcDescription(start, end, radius, isLargeArc) {
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${isLargeArc} 0 ${end.x} ${end.y}`;
  }

  /*
    Makes a series of Path elements.

    Specifically, it keeps track of a start and end angle that are updated while looping thru
    each data item passed into the component.

    For each data item, an angle is computed based off its supplied value. Based on the resulting angle
    value, polar coordinates on a circle's edge are computed via `getPolarCoordinates`. This gives us an x,y value
    on a circle.

    To draw a line, we compute polar coordinates for a start angle AND an end angle, as the SVG path needs a startX, startY, as
    well as an endX, endY.

    We only want a single section of the circle at a time, because we're rendering a pie chart. If we just compute polar coords based on
    the angle, the start x/y value we need to draw a line won't change. We have a couple loop vars representing a start and end angle so we
    can move that slice - computing x/y values for the appropriate section each loop iteration.
  */
  makePaths(centerX, centerY, radius) {
    let arcAngleStart = 0;
    let arcAngleEnd = 0;

    return this.props.data.map((datum, key) => {
      let datumAngleInDegrees = Math.trunc(360 * (datum.get('value') / 100));
      if (datumAngleInDegrees === 360) {
        datumAngleInDegrees = 359.99;
      }
      const isLargeArc = datumAngleInDegrees <= 180 ? '0' : '1';

      // New angle end computed each loop iteration.
      arcAngleEnd += datumAngleInDegrees;
      // likely some rounding error along the way
      if (arcAngleEnd > 360) {
        arcAngleEnd = 360;
      }

      const polarStart = this.getPolarCoordinates(radius, arcAngleStart);
      const polarEnd = this.getPolarCoordinates(radius, arcAngleEnd);

      const svgStart = this.translatePolarCoordinates(polarStart, centerX, centerY);
      const svgEnd = this.translatePolarCoordinates(polarEnd, centerX, centerY);

      const pathDescription = this.getPathArcDescription(svgStart, svgEnd, radius, isLargeArc);
      const path = (
        <path
          key={key}
          fill='transparent'
          stroke={datum.get('color')}
          strokeWidth={this.props.strokeWidth}
          d={pathDescription}
        />
      );

      // Update end angle, then return path
      arcAngleStart = arcAngleEnd;
      return path;
    });
  }

  /*
    Very similar to <ProgressCircle>, but renders several paths
  */
  render() {
    if (!process.env.IS_BROWSER) {
      return null;
    }
    const size = this.props.size;
    const center = size / 2;
    const strokeWidth = this.props.strokeWidth;
    const radius = size / 2 - strokeWidth / 2;

    return (
      <div
        className={this.props.className}
        style={{
          width: size + 'px',
          height: size + 'px'
        }}
      >
        <svg width={size} height={size} xmlns='http://www.w3.org/2000/svg'>
          <g>
            <ellipse
              className='pie-chart-v2__bg'
              cx={center}
              cy={center}
              rx={radius}
              ry={radius}
              fill='transparent'
              strokeWidth={strokeWidth}
            />
            {this.makePaths(center, center, radius)}
          </g>
        </svg>
        <div className='pie-chart-v2__children' style={{padding: `${strokeWidth}px`}}>
          {this.props.children}
        </div>
      </div>
    );
  }
}
